import React, { useEffect, useState } from 'react';
import affiliateService from '../../../services/affilate.serrvice';
import { toast } from 'react-toastify';
import AuthService from '../../../services/auth.service';

interface AffiliateData {
  _id: string;
  fullName?: string | null;
  referralCode?: string | null;
  commissionRate?: number | null;
  category?: string | null;
  createdAt?: string | null;
}

interface UserItem {
  _id: string;
  username: string;
  fullName?: string | null;
  referrerCode?: string | null;
  phone?: string | null;
  depositPnl?: number | null;
  betPnl?: number | null;
  totalProfitLoss?: number | null;
  depositCount?: number | null;
  totalBets?: number | null;
  isLogin?: boolean | null;
  commission?: {
    depositCommission?: number | null;
    betCommission?: number | null;
    total?: number | null;
  };
  createdAt?: string | null;
}

const Dashboard: React.FC = () => {
  const [affiliate, setAffiliate] = useState<AffiliateData | null>(null);
  const [users, setUsers] = useState<UserItem[]>([]);
  const [loading, setLoading] = useState(false);

  const [totalReferrals, setTotalReferrals] = useState(0);
  const [activeReferrals, setActiveReferrals] = useState(0);
  const [totalCommission, setTotalCommission] = useState(0);
  const [todayCommission, setTodayCommission] = useState(0);

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  useEffect(() => {
    const loadAffiliateData = async () => {
      const token = AuthService.getToken(); // gets correct affiliate token automatically

      if (!token) {
        toast.error('Please login as affiliate first');
        return;
      }

      setLoading(true);
      try {
        const res = await affiliateService.getAffiliateUsers();
        console.log('Affiliate API Response:', res);

        if (res.data && res.data.code === 200 && !res.data.error) {
          const affiliateData: AffiliateData = res.data.data?.affiliate || null;
          const fetchedUsers: UserItem[] = res.data.data?.users || [];

          setAffiliate(affiliateData);
          setUsers(fetchedUsers);

          // Compute totals
          setTotalReferrals(fetchedUsers.length);
          setActiveReferrals(fetchedUsers.filter(u => u.isLogin ?? false).length);
          setTotalCommission(
            fetchedUsers.reduce((sum, u) => sum + (u.commission?.total ?? 0), 0)
          );

          const today = new Date();
          const todayComm = fetchedUsers.reduce((sum, u) => {
            const created = u.createdAt ? new Date(u.createdAt) : null;
            if (
              created &&
              created.getDate() === today.getDate() &&
              created.getMonth() === today.getMonth() &&
              created.getFullYear() === today.getFullYear()
            ) {
              return sum + (u.commission?.total ?? 0);
            }
            return sum;
          }, 0);
          setTodayCommission(todayComm);
        } else {
          toast.error(res.data?.message || 'Failed to load affiliate users');
        }
      } catch (err) {
        console.error('Failed to load affiliate users:', err);
        toast.error('Failed to fetch affiliate users');
      } finally {
        setLoading(false);
      }
    };

    // Call API only if affiliate is logged in
    if (AuthService.isAffiliateLoggedIn()) {
      loadAffiliateData();
    } else {
      toast.warn('Affiliate not logged in');
    }
  }, []);

  // Styles
  const containerStyle: React.CSSProperties = { padding: '20px', backgroundColor: '#f4f6f8', minHeight: '100vh', fontFamily: 'Arial, sans-serif', marginTop:'44px' };
  const headerStyle: React.CSSProperties = { fontSize: '28px', fontWeight: 'bold', marginBottom: '20px', color: '#333' };
  const cardContainerStyle: React.CSSProperties = { display: 'flex', gap: '20px', flexWrap: 'wrap' };
  const cardStyle: React.CSSProperties = { flex: '1 1 200px', backgroundColor: '#ffffff', padding: '20px', borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', textAlign: 'center' };
  const cardTitleStyle: React.CSSProperties = { fontSize: '18px', color: '#555', marginBottom: '10px' };
  const cardValueStyle: React.CSSProperties = { fontSize: '24px', fontWeight: 'bold', color: '#2a9d8f' };
  const profileContainerStyle: React.CSSProperties = { backgroundColor: '#ffffff', padding: '20px', borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', marginBottom: '20px' };
  const profileDetailStyle: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '10px', margin: '10px 0' };
  const labelStyle: React.CSSProperties = { fontWeight: 'bold', color: '#555' };

  return (
    <div style={containerStyle}>
      <h1 style={headerStyle}>Affiliate Dashboard</h1>

      {affiliate ? (
        <div style={profileContainerStyle}>
          <h2 style={cardTitleStyle}>Affiliate Profile</h2>
          <div style={profileDetailStyle}>
            <span style={labelStyle}>Full Name:</span>
            <span>{affiliate.fullName ?? '-'}</span>

            <span style={labelStyle}>Referral Code:</span>
            <span>{affiliate.referralCode ?? '-'}</span>

            <span style={labelStyle}>Category:</span>
            <span>{affiliate.category ?? '-'}</span>

            <span style={labelStyle}>Commission Rate:</span>
            <span>{affiliate.commissionRate ?? 0}%</span>

            <span style={labelStyle}>Created At:</span>
            <span>{formatDate(affiliate.createdAt)}</span>
          </div>
        </div>
      ) : (
        <p style={{ color: '#888' }}>No affiliate data available.</p>
      )}

      {/* Statistics Cards */}
      <div style={cardContainerStyle}>
        <div style={cardStyle}>
          <div style={cardTitleStyle}>Total Referrals</div>
          <div style={cardValueStyle}>{loading ? 'Loading...' : totalReferrals}</div>
        </div>
        <div style={cardStyle}>
          <div style={cardTitleStyle}>Active Referrals</div>
          <div style={cardValueStyle}>{loading ? 'Loading...' : activeReferrals}</div>
        </div>
        <div style={cardStyle}>
          <div style={cardTitleStyle}>Total Commission</div>
          <div style={cardValueStyle}>₹{loading ? '0' : totalCommission.toFixed(2)}</div>
        </div>
        <div style={cardStyle}>
          <div style={cardTitleStyle}>Today's Commission</div>
          <div style={cardValueStyle}>₹{loading ? '0' : todayCommission.toFixed(2)}</div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
